import React, { useState, useEffect, useMemo, useRef } from 'react';
import { get, post, put, del } from 'aws-amplify/api';
import './TaskManagement.css';
import { getLocalDateString } from '../utils/date';

// タスクカラープリセット
const PRESET_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#6366F1',
  '#14B8A6', '#F43F5E', '#A855F7', '#22C55E', '#EAB308',
  '#64748B', '#DC2626', '#16A34A', '#D97706', '#7C3AED',
];

// フォルダアイコン
const FolderIcon = ({ isOpen }) => isOpen ? '📂' : '📁';

// ========== CSV解析 ==========

// クォート対応のCSV1行パーサー
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

// CSVテキスト全体をパースして rows 配列を返す
function parseCsv(text) {
  // BOM除去、改行統一
  const clean = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = clean.split('\n').filter(l => l.trim());
  if (lines.length < 2) return { error: 'ヘッダー行とデータ行が必要です' };

  const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/"/g, ''));
  if (!headers.includes('task_name')) {
    return { error: '必須列 "task_name" がヘッダーにありません' };
  }

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const row = {};
    headers.forEach((h, idx) => {
      row[h] = (values[idx] || '').replace(/^"|"$/g, '').trim();
    });
    if (row.task_name) rows.push(row);
  }

  if (rows.length === 0) return { error: 'インポートするタスクがありません' };
  return { rows };
}

// フォルダ/タスクの子孫IDを再帰取得（循環参照・深度制限チェック用）
function getDescendantIds(taskId, allTasks) {
  const ids = new Set([taskId]);
  const queue = [taskId];
  while (queue.length > 0) {
    const current = queue.shift();
    allTasks.filter(t => t.parent_id === current).forEach(t => {
      if (!ids.has(t.task_id)) {
        ids.add(t.task_id);
        queue.push(t.task_id);
      }
    });
  }
  return ids;
}

// ========== ツリー構築 ==========
function buildTree(items) {
  const map = {};
  const roots = [];

  items.forEach(item => { map[item.task_id] = { ...item, children: [] }; });
  items.forEach(item => {
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push(map[item.task_id]);
    } else {
      roots.push(map[item.task_id]);
    }
  });

  const sortItems = (arr) => {
    arr.sort((a, b) => {
      if (a.item_type === b.item_type) return a.task_name.localeCompare(b.task_name, 'ja');
      return a.item_type === 'folder' ? -1 : 1;
    });
    arr.forEach(item => sortItems(item.children));
    return arr;
  };

  return sortItems(roots);
}

// ========== メインコンポーネント ==========
function TaskManagement({ onTasksUpdated, isAdmin }) {
  // 既存のstate
  const [tasks, setTasks] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    task_name: '', description: '', color: '#3B82F6', parent_id: '', item_type: 'task',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [movingTask, setMovingTask] = useState(null);
  const [moveTargetId, setMoveTargetId] = useState('');

  // アーカイブ表示
  const [showArchived, setShowArchived] = useState(false);

  // CSVモーダル
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvStep, setCsvStep] = useState(1); // 1: アップロード, 2: プレビュー, 3: 完了
  const [csvRows, setCsvRows] = useState([]);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvError, setCsvError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // トースト通知
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const toastTimerRef = useRef(null);

  // 確認ダイアログ
  const [confirmState, setConfirmState] = useState({ visible: false, message: '', onConfirm: null });

  const showToast = (message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ visible: true, message, type });
    toastTimerRef.current = setTimeout(() => setToast(t => ({ ...t, visible: false })), 3500);
  };

  const showConfirm = (message, onConfirm) => {
    setConfirmState({ visible: true, message, onConfirm });
  };

  // 確認ダイアログ開放時に背景スクロール禁止
  useEffect(() => {
    document.body.style.overflow = (confirmState.visible || showCsvModal) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [confirmState.visible, showCsvModal]);

  useEffect(() => {
    fetchTasks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const fetchTasks = async () => {
    setIsLoading(true);
    try {
      const restOperation = get({
        apiName: 'worktimerApi',
        path: '/tasks',
        ...(showArchived ? { options: { queryParams: { include_archived: 'true' } } } : {}),
      });
      const response = await restOperation.response;
      const data = await response.body.json();
      setTasks(data);
      const folderIds = data.filter(t => t.item_type === 'folder').map(t => t.task_id);
      setExpandedFolders(new Set(folderIds));
      if (onTasksUpdated) onTasksUpdated();
    } catch (e) {
      console.error("❌ タスク取得エラー:", e);
      showToast("タスクの取得に失敗しました", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ========== CRUD ==========

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.task_name.trim()) { showToast("名前を入力してください", "error"); return; }
    setIsLoading(true);
    try {
      if (editingTask) {
        const restOperation = put({
          apiName: 'worktimerApi',
          path: `/tasks/${editingTask.task_id}`,
          options: { body: formData }
        });
        await restOperation.response;
        showToast("更新しました");
      } else {
        const restOperation = post({
          apiName: 'worktimerApi',
          path: '/tasks',
          options: { body: formData }
        });
        await restOperation.response;
        showToast(`${formData.item_type === 'folder' ? 'フォルダ' : 'タスク'}を作成しました`);
      }
      resetForm();
      fetchTasks();
    } catch (e) {
      console.error("❌ 保存エラー:", e);
      showToast("保存に失敗しました", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      task_name: task.task_name,
      description: task.description || '',
      color: task.color || '#3B82F6',
      parent_id: task.parent_id || '',
      item_type: task.item_type || 'task',
    });
    setShowAddForm(true);
  };

  const handleDelete = async (taskId, itemType) => {
    const label = itemType === 'folder' ? 'フォルダ' : 'タスク';
    const folderWarning = itemType === 'folder'
      ? '\n⚠️ フォルダ配下のタスクも全てアーカイブされます。' : '';
    showConfirm(
      `この${label}をアーカイブしてもよろしいですか？${folderWarning}\n過去の作業ログには影響しません。アーカイブ後は「アーカイブ表示」から確認・復元できます。`,
      async () => {
        setIsLoading(true);
        try {
          const restOperation = del({ apiName: 'worktimerApi', path: `/tasks/${taskId}` });
          await restOperation.response;
          showToast(`${label}をアーカイブしました`);
          fetchTasks();
        } catch (e) {
          console.error("❌ アーカイブエラー:", e);
          showToast("アーカイブに失敗しました", "error");
        } finally {
          setIsLoading(false);
        }
      }
    );
  };

  // アーカイブ済みタスクを復元
  const handleRestore = (taskId) => {
    showConfirm('このタスクを復元してもよろしいですか？', async () => {
      setIsLoading(true);
      try {
        const restOperation = put({
          apiName: 'worktimerApi',
          path: `/tasks/${taskId}`,
          options: { body: { is_active: true } }
        });
        await restOperation.response;
        showToast('タスクを復元しました');
        fetchTasks();
      } catch (e) {
        console.error("❌ 復元エラー:", e);
        showToast("復元に失敗しました", "error");
      } finally {
        setIsLoading(false);
      }
    });
  };

  const handleMove = async () => {
    if (!movingTask) return;
    setIsLoading(true);
    try {
      const restOperation = put({
        apiName: 'worktimerApi',
        path: `/tasks/${movingTask.task_id}`,
        options: { body: { parent_id: moveTargetId || null } }
      });
      await restOperation.response;
      showToast("移動しました");
      setMovingTask(null);
      fetchTasks();
    } catch (e) {
      console.error("❌ 移動エラー:", e);
      showToast("移動に失敗しました", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setShowAddForm(false);
    setEditingTask(null);
    setFormData({ task_name: '', description: '', color: '#3B82F6', parent_id: '', item_type: 'task' });
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      next.has(folderId) ? next.delete(folderId) : next.add(folderId);
      return next;
    });
  };

  // ========== CSV機能 ==========

  const handleCsvFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setCsvError('.csv ファイルを選択してください');
      return;
    }
    setCsvFileName(file.name);
    setCsvError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = parseCsv(e.target.result);
      if (result.error) { setCsvError(result.error); return; }
      setCsvRows(result.rows);
      setCsvStep(2);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleCsvFile(e.dataTransfer.files[0]);
  };

  const handleImport = async () => {
    setIsImporting(true);
    try {
      const restOperation = post({
        apiName: 'worktimerApi',
        path: '/tasks/import-csv',
        options: { body: { rows: csvRows } }
      });
      const response = await restOperation.response;
      const data = await response.body.json();
      setImportResult(data);
      setCsvStep(3);
      fetchTasks();
    } catch (e) {
      console.error("❌ インポートエラー:", e);
      showToast("インポートに失敗しました", "error");
    } finally {
      setIsImporting(false);
    }
  };

  // 現在のアクティブタスクをCSVでエクスポート
  const handleExport = () => {
    const activeTasks = tasks.filter(t => t.item_type === 'task' && t.is_active);
    const esc = (s) => {
      if (!s) return '';
      return (s.includes(',') || s.includes('"') || s.includes('\n'))
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = 'folder_name,task_name,description,color';
    const lines = activeTasks.map(t => {
      const folder = tasks.find(f => f.task_id === t.parent_id);
      return [esc(folder ? folder.task_name : ''), esc(t.task_name), esc(t.description || ''), t.color || '#3B82F6'].join(',');
    });
    const csv = '\uFEFF' + [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tasks_${getLocalDateString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // サンプルCSVをダウンロード
  const downloadSample = () => {
    const content = [
      '\uFEFFfolder_name,task_name,description,color',
      '日常業務,メール対応,メールの確認と返信,#3B82F6',
      '日常業務,議事録作成,,#10B981',
      'プロジェクトA,資料作成,プレゼン資料の作成,#F59E0B',
      ',その他の作業,,#64748B',
    ].join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tasks_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const closeCsvModal = () => {
    setShowCsvModal(false);
    setCsvStep(1);
    setCsvRows([]);
    setCsvFileName('');
    setCsvError('');
    setImportResult(null);
  };

  // プレビュー時: CSVにない現在のアクティブタスク一覧（アーカイブ予定）
  const willBeArchived = useMemo(() => {
    if (csvRows.length === 0) return [];
    const csvNames = new Set(csvRows.map(r => r.task_name?.trim()).filter(Boolean));
    return tasks.filter(t => t.item_type === 'task' && t.is_active && !csvNames.has(t.task_name));
  }, [csvRows, tasks]);

  // ツリー用データ: showArchived=true のときはアーカイブ含む全件
  const displayTasks = showArchived ? tasks : tasks.filter(t => t.is_active);
  const tree = buildTree(displayTasks);

  // フォルダ選択UI用: アクティブフォルダをツリー順にフラット化（深度情報付き）
  const flattenedFolders = useMemo(() => {
    const result = [];
    const activeTree = buildTree(tasks.filter(t => t.is_active));
    const flatten = (items, depth = 0) => {
      items.filter(i => i.item_type === 'folder').forEach(item => {
        result.push({ ...item, depth });
        if (item.children?.length) flatten(item.children, depth + 1);
      });
    };
    flatten(activeTree);
    return result;
  }, [tasks]);

  // ========== ツリー描画 ==========
  const renderTreeItem = (item, depth = 0) => {
    const isFolder = item.item_type === 'folder';
    const isExpanded = expandedFolders.has(item.task_id);
    const isArchived = !item.is_active;
    const indent = depth * 20;

    return (
      <React.Fragment key={item.task_id}>
        <div
          className={[
            'task-list-item',
            `task-list-item--${isFolder ? 'folder' : 'task'}`,
            isArchived ? 'task-list-item--archived' : '',
          ].join(' ')}
          style={{ paddingLeft: `${12 + indent}px` }}
        >
          {isFolder && (
            <button
              className="folder-toggle-btn"
              onClick={() => toggleFolder(item.task_id)}
              title={isExpanded ? '折りたたむ' : '展開する'}
            >
              <FolderIcon isOpen={isExpanded} />
            </button>
          )}
          {!isFolder && (
            <span
              className="task-color-swatch"
              style={{ background: isArchived ? '#D1D5DB' : (item.color || '#9CA3AF') }}
            />
          )}
          <div className="task-list-info">
            <span className={`task-list-name ${isFolder ? 'task-list-name--folder' : ''} ${isArchived ? 'task-list-name--archived' : ''}`}>
              {item.task_name}
              {isArchived && <span className="archive-badge">アーカイブ</span>}
            </span>
            {item.description && (
              <span className="task-list-desc">{item.description}</span>
            )}
            {isArchived && item.archived_at && (
              <span className="task-list-desc archived-date">
                アーカイブ日: {new Date(item.archived_at).toLocaleDateString('ja-JP')}
              </span>
            )}
          </div>
          <div className="task-list-meta">
            <span className="task-date">
              {new Date(item.created_at).toLocaleDateString('ja-JP')}
            </span>
            {isAdmin && (
              <div className="task-actions">
                {isArchived ? (
                  <button
                    onClick={() => handleRestore(item.task_id)}
                    className="task-icon-btn task-icon-btn--restore"
                    title="復元"
                    disabled={isLoading}
                  >♻️</button>
                ) : (
                  <>
                    <button onClick={() => handleEdit(item)} className="task-icon-btn" title="編集" disabled={isLoading}>✏️</button>
                    <button
                      onClick={() => { setMovingTask(item); setMoveTargetId(item.parent_id || ''); }}
                      className="task-icon-btn"
                      title="移動"
                      disabled={isLoading}
                    >📂</button>
                    <button onClick={() => handleDelete(item.task_id, item.item_type)} className="task-icon-btn task-icon-btn--delete" title="アーカイブ" disabled={isLoading}>🗂️</button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {isFolder && isExpanded && item.children.map(child => renderTreeItem(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="task-management">
      <div className="section-header">
        <h2>📋 タスク管理</h2>
        {isAdmin && (
          <div className="header-btn-group">
            <button
              className={`toggle-archive-btn${showArchived ? ' toggle-archive-btn--active' : ''}`}
              onClick={() => setShowArchived(!showArchived)}
              title="アーカイブ済みタスクの表示切替"
            >
              🗂️ {showArchived ? 'アーカイブ表示中' : 'アーカイブ表示'}
            </button>
            <button
              className="csv-export-btn"
              onClick={handleExport}
              disabled={isLoading}
              title="現在のタスクをCSVでダウンロード"
            >
              ⬇️ CSV出力
            </button>
            <button
              className="csv-import-btn"
              onClick={() => { setShowCsvModal(true); setCsvStep(1); }}
              disabled={isLoading}
            >
              ⬆️ CSVインポート
            </button>
            <button
              className="edit-button"
              onClick={() => { setFormData(f => ({ ...f, item_type: 'folder' })); setShowAddForm(true); }}
              disabled={isLoading}
            >
              ➕ 新規フォルダ
            </button>
            <button
              className="edit-button"
              onClick={() => { setFormData(f => ({ ...f, item_type: 'task' })); setShowAddForm(true); }}
              disabled={isLoading}
            >
              ➕ 新規タスク
            </button>
          </div>
        )}
      </div>

      {/* 作成・編集ダイアログ */}
      {showAddForm && (
        <div className="task-dialog-overlay" onClick={resetForm}>
          <div className="task-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="task-dialog-header">
              <h3>
                {editingTask
                  ? `✏️ ${editingTask.item_type === 'folder' ? 'フォルダ' : 'タスク'}編集`
                  : `➕ 新規${formData.item_type === 'folder' ? 'フォルダ' : 'タスク'}作成`}
              </h3>
              <button className="task-dialog-close" onClick={resetForm}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="task-dialog-body">
                <div className="task-form-group">
                  <label htmlFor="task_name">
                    {formData.item_type === 'folder' ? 'フォルダ名' : 'タスク名'} *
                  </label>
                  <input
                    type="text"
                    id="task_name"
                    value={formData.task_name}
                    onChange={(e) => setFormData({ ...formData, task_name: e.target.value })}
                    placeholder={formData.item_type === 'folder' ? '例: プロジェクトA' : '例: メール対応'}
                    required
                    maxLength={100}
                    disabled={isLoading}
                  />
                </div>
                <div className="task-form-group">
                  <label htmlFor="description">説明</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="詳細説明（任意）"
                    rows={3}
                    disabled={isLoading}
                  />
                </div>
                {/* フォルダ・タスク共通: 親フォルダ選択 */}
                <div className="task-form-group">
                  <label htmlFor="parent_folder">
                    {formData.item_type === 'folder' ? '親フォルダ（任意・最大3階層）' : 'フォルダ（任意）'}
                  </label>
                  <select
                    id="parent_folder"
                    value={formData.parent_id}
                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                    disabled={isLoading}
                  >
                    <option value="">
                      {formData.item_type === 'folder' ? '親フォルダなし（ルート）' : 'フォルダなし（ルート）'}
                    </option>
                    {flattenedFolders
                      .filter(f => !editingTask || !getDescendantIds(editingTask.task_id, tasks).has(f.task_id))
                      .map(f => {
                        // フォルダ作成・編集時: 深度2以上は親にできない
                        const isDisabled = formData.item_type === 'folder' && f.depth >= 2;
                        return (
                          <option key={f.task_id} value={f.task_id} disabled={isDisabled}>
                            {'　'.repeat(f.depth)}📁 {f.task_name}{isDisabled ? ' (最大階層)' : ''}
                          </option>
                        );
                      })
                    }
                  </select>
                </div>
                {/* タスクのみ: カラー選択 */}
                {formData.item_type === 'task' && (
                  <div className="task-form-group">
                    <label>タスクカラー</label>
                      <div className="color-picker-section">
                        <div className="preset-colors">
                          {PRESET_COLORS.map(c => (
                            <button
                              key={c}
                              type="button"
                              className={`preset-color-btn ${formData.color === c ? 'selected' : ''}`}
                              style={{ background: c }}
                              onClick={() => setFormData({ ...formData, color: c })}
                              disabled={isLoading}
                              title={c}
                            />
                          ))}
                        </div>
                        <div className="custom-color-row">
                          <label htmlFor="custom-color">カスタム：</label>
                          <input
                            type="color"
                            id="custom-color"
                            value={formData.color || '#3B82F6'}
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                            disabled={isLoading}
                            className="custom-color-input"
                          />
                          <span className="color-hex-label">{formData.color || '#3B82F6'}</span>
                        </div>
                      </div>
                  </div>
                )}

              </div>
              <div className="task-dialog-footer">

                <button type="button" onClick={resetForm} className="cancel-button" disabled={isLoading}>
                  キャンセル
                </button>
                <button type="submit" className="submit-button" disabled={isLoading}>
                  {isLoading ? '保存中...' : (editingTask ? '更新' : '作成')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 移動ダイアログ */}
      {movingTask && (
        <div className="task-dialog-overlay" onClick={() => setMovingTask(null)}>
          <div className="task-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="task-dialog-header">
              <h3>📂 {movingTask?.item_type === 'folder' ? 'フォルダ' : 'タスク'}を移動</h3>
              <button className="task-dialog-close" onClick={() => setMovingTask(null)}>✕</button>
            </div>
            <div className="task-dialog-body">
              <p style={{ marginBottom: 12, color: '#374151' }}>
                <strong>「{movingTask.task_name}」</strong> の移動先を選択してください
              </p>
              <div className="task-form-group">
                <label htmlFor="move-target">移動先フォルダ</label>
                <select
                  id="move-target"
                  value={moveTargetId}
                  onChange={(e) => setMoveTargetId(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">フォルダなし（ルート）</option>
                  {flattenedFolders
                    .filter(f => {
                      // フォルダ移動時: 自分自身と子孫は選択不可（循環参照防止）
                      if (movingTask?.item_type === 'folder') {
                        return !getDescendantIds(movingTask.task_id, tasks).has(f.task_id);
                      }
                      return true;
                    })
                    .map(f => {
                      // フォルダ移動時: 深度2以上の親は選択不可（3階層制限）
                      const isDisabled = movingTask?.item_type === 'folder' && f.depth >= 2;
                      return (
                        <option key={f.task_id} value={f.task_id} disabled={isDisabled}>
                          {'　'.repeat(f.depth)}📁 {f.task_name}{isDisabled ? ' (最大階層)' : ''}
                        </option>
                      );
                    })
                  }
                </select>
              </div>
            </div>
            <div className="task-dialog-footer">
              <button type="button" onClick={() => setMovingTask(null)} className="cancel-button" disabled={isLoading}>
                キャンセル
              </button>
              <button type="button" onClick={handleMove} className="submit-button" disabled={isLoading}>
                {isLoading ? '移動中...' : '移動'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== CSVインポートモーダル ========== */}
      {showCsvModal && (
        <div className="task-dialog-overlay" onClick={closeCsvModal}>
          <div className="task-dialog csv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="task-dialog-header">
              <h3>
                {csvStep === 1 && '⬆️ CSVインポート'}
                {csvStep === 2 && '📋 インポートプレビュー'}
                {csvStep === 3 && '✅ インポート完了'}
              </h3>
              <button className="task-dialog-close" onClick={closeCsvModal}>✕</button>
            </div>

            {/* ステップ1: ファイル選択 */}
            {csvStep === 1 && (
              <div className="task-dialog-body">
                <div
                  className={`csv-drop-zone${isDragOver ? ' csv-drop-zone--active' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="csv-drop-icon">📄</div>
                  <p className="csv-drop-text">CSVファイルをドラッグ＆ドロップ</p>
                  <p className="csv-drop-sub">またはクリックして選択</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={(e) => { handleCsvFile(e.target.files[0]); e.target.value = ''; }}
                  />
                </div>

                {csvError && <div className="csv-error-msg">⚠️ {csvError}</div>}

                <div className="csv-format-box">
                  <div className="csv-format-title">CSVフォーマット</div>
                  <div className="csv-format-code">folder_name,task_name,description,color</div>
                  <ul className="csv-format-list">
                    <li><strong>folder_name</strong>: フォルダ名（省略可。空欄でルートに配置）</li>
                    <li><strong>task_name</strong>: タスク名（<span className="csv-required">必須</span>）</li>
                    <li><strong>description</strong>: 説明（省略可）</li>
                    <li><strong>color</strong>: カラーコード（省略可。例: #3B82F6）</li>
                  </ul>
                  <div className="csv-format-note">
                    ※ CSVにないアクティブタスクは自動的にアーカイブされます
                  </div>
                  <button className="csv-sample-btn" onClick={downloadSample}>
                    📥 サンプルCSVをダウンロード
                  </button>
                </div>
              </div>
            )}

            {/* ステップ2: プレビュー */}
            {csvStep === 2 && (
              <div className="task-dialog-body csv-preview-body">
                <div className="csv-preview-header">
                  <span className="csv-preview-file">📄 {csvFileName}</span>
                  <span className="csv-preview-count">{csvRows.length} 件のタスク</span>
                </div>

                <div className="csv-preview-scroll">
                  <table className="csv-preview-table">
                    <thead>
                      <tr>
                        <th>フォルダ</th>
                        <th>タスク名</th>
                        <th>説明</th>
                        <th>カラー</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvRows.map((row, i) => (
                        <tr key={i}>
                          <td className="csv-folder-cell">
                            {row.folder_name
                              ? <><span className="csv-folder-icon">📁</span>{row.folder_name}</>
                              : <span className="csv-no-val">（なし）</span>}
                          </td>
                          <td><strong>{row.task_name}</strong></td>
                          <td className="csv-desc-cell">{row.description || <span className="csv-no-val">—</span>}</td>
                          <td>
                            <span className="csv-color-dot" style={{ background: row.color || '#3B82F6' }} />
                            <span className="csv-color-code">{row.color || '#3B82F6'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {willBeArchived.length > 0 && (
                  <div className="csv-archive-warning">
                    <div className="csv-archive-warning-title">
                      ⚠️ 以下の {willBeArchived.length} 件がアーカイブされます
                    </div>
                    <ul className="csv-archive-list">
                      {willBeArchived.map(t => <li key={t.task_id}>{t.task_name}</li>)}
                    </ul>
                  </div>
                )}

                <div className="csv-preview-actions">
                  <button className="cancel-button" onClick={() => setCsvStep(1)} disabled={isImporting}>
                    ← 戻る
                  </button>
                  <button className="submit-button" onClick={handleImport} disabled={isImporting}>
                    {isImporting ? 'インポート中...' : `${csvRows.length} 件をインポート`}
                  </button>
                </div>
              </div>
            )}

            {/* ステップ3: 完了 */}
            {csvStep === 3 && importResult && (
              <div className="task-dialog-body csv-result-body">
                <div className="csv-result-icon">✅</div>
                <div className="csv-result-title">インポートが完了しました</div>
                <div className="csv-result-stats">
                  <div className="csv-result-stat">
                    <span className="csv-result-num">{importResult.imported}</span>
                    <span className="csv-result-label">件をインポート</span>
                  </div>
                  {importResult.archived > 0 && (
                    <div className="csv-result-stat">
                      <span className="csv-result-num csv-result-num--archive">{importResult.archived}</span>
                      <span className="csv-result-label">件をアーカイブ</span>
                    </div>
                  )}
                </div>
                {importResult.archived > 0 && (
                  <div className="csv-result-archived-list">
                    <div className="csv-result-archived-title">アーカイブされたタスク:</div>
                    <ul>
                      {importResult.archived_tasks.map((name, i) => <li key={i}>{name}</li>)}
                    </ul>
                  </div>
                )}
                <div className="csv-preview-actions">
                  <button className="submit-button" onClick={closeCsvModal}>閉じる</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isLoading && !showAddForm && !movingTask && !showCsvModal && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>読み込み中...</p>
        </div>
      )}

      {!isLoading && tasks.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>タスクがまだありません</h3>
          <p>「新規タスク」ボタンから最初のタスクを作成しましょう</p>
        </div>
      )}

      {!isLoading && tree.length > 0 && (
        <div className="tasks-list">
          {tree.map(item => renderTreeItem(item, 0))}
        </div>
      )}

      {/* トースト通知 */}
      {toast.visible && (
        <div className={`tm-toast tm-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* 確認ダイアログ */}
      {confirmState.visible && (
        <div className="tm-confirm-overlay">
          <div className="tm-confirm-dialog">
            <p className="tm-confirm-message">{confirmState.message}</p>
            <div className="tm-confirm-actions">
              <button
                className="tm-confirm-cancel"
                onClick={() => setConfirmState({ visible: false, message: '', onConfirm: null })}
              >
                キャンセル
              </button>
              <button
                className="tm-confirm-ok"
                onClick={() => {
                  const fn = confirmState.onConfirm;
                  setConfirmState({ visible: false, message: '', onConfirm: null });
                  if (fn) fn();
                }}
              >
                実行する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskManagement;
